import scssVariables from '../../../../Config.module.scss';
import formattingService from '../../../Services/Formatting/FormattingService';
import languageService from '../../../Services/Language/LanguageService';
import { translationKeys } from '../../../Services/Language/TranslationDefinitions';
import styleService from '../../../Services/Styles/StyleService';
import { TableHeader } from '../Table/TableDefinitions';
import styles from './SeasonalTables.module.scss';

export type SeasonalTableReferenceType = 'valueCsv' | 'valueXlsx' | 'percentCsv' | 'percentXlsx';
export type SeasonalColumnDefinition = TableHeader<SeasonalModel> & { isAverage?: boolean } & { isYearly?: boolean };
export type AverageOrTotalsType = 'average' | 'total';

export type MonthIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type MonthKey = `month${MonthIndex}`;
type MonthActualKey = `${MonthKey}IsActualValue`;

export type SeasonalModel = {
    id: string;
    isPercent: boolean;
    displayDecimalPlacesMinimum?: number | null | undefined;
    displayDecimalPlacesMaximum?: number | null | undefined;
    year: number | string;
    quarter1?: number;
    quarter1IsActualValue?: boolean;
    quarter2?: number;
    quarter2IsActualValue?: boolean;
    quarter3?: number;
    quarter3IsActualValue?: boolean;
    quarter4?: number;
    quarter4IsActualValue?: boolean;
    yearlyAverageOrTotal?: number;
    yearlyAverageOrTotalIsActualValue?: boolean;
} & Partial<Record<MonthKey, number>> &
    Partial<Record<MonthActualKey, boolean>>;

export type RendererParameters = {
    data: SeasonalModel;
    value: string;
    column: { colId: keyof SeasonalModel; colDef: { field: keyof SeasonalModel } };
};

const cellClassSetter = (parameters: SeasonalModel, header: SeasonalColumnDefinition) => {
    const cellStyles = [styles.seasonal_table_cell];

    if (header.field === 'year') {
        return cellStyles;
    }

    if (parameters.isPercent && header?.field) {
        cellStyles.push(...styleService.positiveOrNegativeCellClassSetter({ value: parameters[header.field as keyof SeasonalModel] as string }));
    }

    if (!parameters[`${header.field}IsActualValue` as keyof SeasonalModel]) {
        cellStyles.push(styles.seasonal_table_cell_forecast_value);
    }

    return cellStyles;
};

export const seasonalTableDefaultColumnOptions = {
    handleGetCellClasses: cellClassSetter,
    minimumWidth: 60,
};

export const seasonalTableBase: SeasonalColumnDefinition[] = [
    {
        headerValueGetter: () => languageService.translate(translationKeys.charts.text.monthly),
        pinned: 'left',
        children: [
            {
                pinned: 'left',
                field: 'year',
                cellStyle: { color: scssVariables.mainTextColor, fontWeight: 700 },
                headerValueGetter: () => languageService.translate(translationKeys.words.year),
            },
            {
                field: 'month1',
                headerValueGetter: () => formattingService.toMonth(1),
                dataFormat: 'currency',
            },
            {
                field: 'month2',
                headerValueGetter: () => formattingService.toMonth(2),
                dataFormat: 'currency',
            },
            {
                field: 'month3',
                headerValueGetter: () => formattingService.toMonth(3),
                dataFormat: 'currency',
            },
            {
                field: 'month4',
                headerValueGetter: () => formattingService.toMonth(4),
                dataFormat: 'currency',
            },
            {
                field: 'month5',
                headerValueGetter: () => formattingService.toMonth(5),
                dataFormat: 'currency',
            },
            {
                field: 'month6',
                headerValueGetter: () => formattingService.toMonth(6),
                dataFormat: 'currency',
            },
            {
                field: 'month7',
                headerValueGetter: () => formattingService.toMonth(7),
                dataFormat: 'currency',
            },
            {
                field: 'month8',
                headerValueGetter: () => formattingService.toMonth(8),
                dataFormat: 'currency',
            },
            {
                field: 'month9',
                headerValueGetter: () => formattingService.toMonth(9),
                dataFormat: 'currency',
            },
            {
                field: 'month10',
                headerValueGetter: () => formattingService.toMonth(10),
                dataFormat: 'currency',
            },
            {
                field: 'month11',
                headerValueGetter: () => formattingService.toMonth(11),
                dataFormat: 'currency',
            },
            {
                field: 'month12',
                headerValueGetter: () => formattingService.toMonth(12),
                dataFormat: 'currency',
            },
        ],
    },
];

export const yearlyAverageOrTotalColumnDefinitions = [
    {
        headerValueGetter: () => languageService.translate(translationKeys.words.quarterly),
        isAverage: true,
        lockPinned: true,
        pinned: 'right',
        hide: true,
        children: [
            {
                cellStyle: { borderLeft: `1px solid ${scssVariables.stonexLightGrayBlue}` },
                field: 'quarter1',
                isAverage: true,
                pinned: 'right',
                hide: true,
                width: 100,
            },
            {
                field: 'quarter2',
                isAverage: true,
                pinned: 'right',
                hide: true,
                width: 100,
            },
            {
                field: 'quarter3',
                isAverage: true,
                pinned: 'right',
                hide: true,
                width: 100,
            },
            {
                field: 'quarter4',
                isAverage: true,
                pinned: 'right',
                hide: true,
                width: 100,
            },
        ],
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.words.yearly),
        field: 'yearlyAverageOrTotal',
        isAverage: true,
        isYearly: true,
        hide: true,
        pinned: 'right',
        width: 150,
        children: [
            {
                field: 'yearlyAverageOrTotal',
                isAverage: true,
                lockPinned: true,
                pinned: 'right',
                hide: true,
                width: 150,
            },
        ],
    },
];
